import React, { useState } from "react";
import styled from "styled-components";
import { palette } from "../../../../assets/styles/Palette";
import { H4 } from "../../../../assets/styles/Typography";
import { createSupportInquiry } from "../../../../utils/indexedDB";

// 목록 갱신을 위한 이벤트 이름(두 파일이 이 문자열로 연결됨)
const INQUIRY_CREATED_EVENT = "supportInquiry:created";

const MoleculeInquiryWrite = ({ isLoggedIn }) => {
  const [inquiryForm, setInquiryForm] = useState({ title: "", content: "", contact: "" });
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [inquiryError, setInquiryError] = useState("");
  const [inquirySuccess, setInquirySuccess] = useState("");

  const handleInquirySubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setInquiryError("로그인이 필요합니다.");
      setInquirySuccess("");
      return;
    }

    const title = inquiryForm.title.trim();
    const content = inquiryForm.content.trim();
    const contact = inquiryForm.contact.trim();

    if (!title || !content || !contact) {
      setInquiryError("제목, 내용, 연락처를 모두 입력해주세요.");
      setInquirySuccess("");
      return;
    }

    setIsSubmittingInquiry(true);
    setInquiryError("");
    setInquirySuccess("");

    try {
      await createSupportInquiry({ title, content, contact });

      setInquirySuccess("문의가 접수되었습니다.");
      setInquiryForm({ title: "", content: "", contact: "" });

      // ✅ 부모 없이도 목록 컴포넌트에 "새 문의 등록됨" 알림
      window.dispatchEvent(new CustomEvent(INQUIRY_CREATED_EVENT));
    } catch (error) {
      setInquiryError("문의 등록에 실패했습니다. 잠시 후 다시 시도해주세요.");
      setInquirySuccess("");
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  return (
    <InquirySection>
      <SectionHeader>
        <H4 color="gray800">문의 작성</H4>
      </SectionHeader>

      <InquiryForm onSubmit={handleInquirySubmit}>
        <InquiryField>
          <label htmlFor="inquiry-title">제목</label>
          <InquiryInput
            id="inquiry-title"
            maxLength={100}
            placeholder="제목을 입력해주세요"
            value={inquiryForm.title}
            onChange={(e) => setInquiryForm((prev) => ({ ...prev, title: e.target.value }))}
          />
        </InquiryField>

        <InquiryField>
          <label htmlFor="inquiry-contact">연락처</label>
          <InquiryInput
            id="inquiry-contact"
            placeholder="답변받을 이메일 또는 전화번호를 입력해주세요"
            value={inquiryForm.contact}
            onChange={(e) => setInquiryForm((prev) => ({ ...prev, contact: e.target.value }))}
          />
        </InquiryField>

        <InquiryField>
          <label htmlFor="inquiry-content">내용</label>
          <InquiryTextarea
            id="inquiry-content"
            rows={5}
            placeholder="문의 내용을 자세히 작성해주세요"
            value={inquiryForm.content}
            onChange={(e) => setInquiryForm((prev) => ({ ...prev, content: e.target.value }))}
          />
        </InquiryField>

        {(inquiryError || inquirySuccess) && (
          <InlineMessage isError={Boolean(inquiryError)}>
            {inquiryError || inquirySuccess}
          </InlineMessage>
        )}

        <InquiryActions>
          <InquiryButton
            type="submit"
            disabled={
              isSubmittingInquiry ||
              !inquiryForm.title.trim() ||
              !inquiryForm.content.trim() ||
              !inquiryForm.contact.trim()
            }
          >
            {isSubmittingInquiry ? "전송 중..." : "문의 보내기"}
          </InquiryButton>
        </InquiryActions>
      </InquiryForm>
    </InquirySection>
  );
};

export default MoleculeInquiryWrite;

/* styles */

const InquirySection = styled.div`
  background: ${palette.white};
  border-radius: 12px;
  padding: 24px;
  border: 1px solid ${palette.outlineGray};
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${palette.outlineGray};
  text-align: left;
`;

const InquiryForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

const InquiryField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: ${palette.gray700};
  }
`;

const InquiryInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${palette.outlineGray};
  font-size: 14px;
  color: ${palette.gray800};
  font-family: "Pretendard";
  font-weight: 400;

  &:focus {
    outline: 2px solid ${palette.primary}33;
    border-color: ${palette.primary};
  }
`;

const InquiryTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${palette.outlineGray};
  font-size: 14px;
  color: ${palette.gray800};
  resize: vertical;
  font-family: "Pretendard";
  font-weight: 400;

  &:focus {
    outline: 2px solid ${palette.primary}33;
    border-color: ${palette.primary};
  }
`;

const InquiryActions = styled.div`
  display: flex;
  justify-content: center;
`;

const InquiryButton = styled.button`
  min-width: 120px;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: ${palette.primary};
  color: ${palette.white};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Pretendard";
  font-weight: 500;
  font-size: 14px;
  margin-top: 8px;

  &:disabled {
    background: ${palette.gray300};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${palette.primary}DD;
  }
`;

const InlineMessage = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${(props) => (props.isError ? `${palette.red}14` : `${palette.primary}14`)};
  color: ${(props) => (props.isError ? palette.red : palette.primary)};
  font-size: 13px;
  border: 1px solid ${(props) => (props.isError ? `${palette.red}55` : `${palette.primary}55`)};
`;
